<template>
  <div class="public-quote-page">
    <div class="public-quote-shell">
      <div class="public-quote-logo">{{ $t('app.name') }}</div>

      <el-card v-loading="loading" shadow="never" class="public-quote-card">
        <div v-if="quote">
          <div v-if="isAccepted" class="celebration" role="status">
            <div class="celebration__confetti" aria-hidden="true" />
            <div class="celebration__title">{{ $t('publicQuote.celebrationTitle') }}</div>
            <div class="celebration__subtitle">{{ $t('publicQuote.celebrationSubtitle') }}</div>
            <div class="celebration__meta">
              <div class="celebration__row">
                <span class="celebration__k">{{ $t('publicQuote.celebrationMetaBusiness') }}</span>
                <span class="celebration__v">{{ $t('app.name') }}</span>
              </div>
              <div class="celebration__row">
                <span class="celebration__k">{{ $t('publicQuote.celebrationMetaAccepted') }}</span>
                <span class="celebration__v">{{ acceptedOnDisplay }}</span>
              </div>
              <div class="celebration__row">
                <span class="celebration__k">{{ $t('publicQuote.celebrationMetaTotal') }}</span>
                <span class="celebration__v">{{ formatUsd(quote.totalAmount) }}</span>
              </div>
            </div>
            <p class="celebration__next">{{ $t('publicQuote.celebrationNext') }}</p>
          </div>

          <div class="estimate-hero">
            <div class="hero-label">{{ $t('publicQuote.estimateTotal') }}</div>
            <div class="hero-amount">{{ formatUsd(quote.totalAmount) }}</div>
            <el-button
              v-if="showDetails"
              type="text"
              class="hero-toggle"
              @click="toggleDetails"
            >
              {{ detailsExpanded ? $t('publicQuote.hideEstimate') : $t('publicQuote.viewEstimate') }}
            </el-button>
          </div>

          <p class="move-forward">{{ introText }}</p>

          <section
            v-show="showDetails && detailsExpanded"
            ref="detailsSection"
            class="estimate-doc"
          >
            <div class="estimate-doc__title">{{ $t('publicQuote.docTitle') }}</div>
            <div class="estimate-doc__meta">
              <div class="estimate-doc__meta-col">
                <div class="estimate-doc__label">{{ $t('publicQuote.docBillTo') }}</div>
                <div class="estimate-doc__value">{{ customerDisplayName }}</div>
              </div>
              <div class="estimate-doc__meta-col estimate-doc__meta-col--right">
                <div class="estimate-doc__kv">
                  <span class="estimate-doc__k">{{ $t('publicQuote.docEstimateNo') }}</span>
                  <span class="estimate-doc__v">{{ quote.quoteNo || '—' }}</span>
                </div>
                <div class="estimate-doc__kv">
                  <span class="estimate-doc__k">{{ $t('publicQuote.docValidThrough') }}</span>
                  <span class="estimate-doc__v">{{ quote.validUntil || '—' }}</span>
                </div>
              </div>
            </div>

            <div class="estimate-doc__table-wrap">
              <div class="estimate-doc__head">
                <span>{{ $t('publicQuote.colIndex') }}</span>
                <span>{{ $t('publicQuote.colProduct') }}</span>
                <span>{{ $t('publicQuote.colDescription') }}</span>
                <span class="estimate-doc__amt">{{ $t('publicQuote.colAmount') }}</span>
              </div>
              <div
                v-for="(item, index) in details"
                :key="item.id || index"
                class="estimate-doc__row"
              >
                <span class="estimate-doc__idx">{{ lineNo(item, index) }}</span>
                <span class="estimate-doc__product">{{ item.product || '—' }}</span>
                <span class="estimate-doc__desc">{{ item.description || '—' }}</span>
                <span class="estimate-doc__amt">{{ formatUsd(item.lineAmount) }}</span>
              </div>
              <div v-if="!details.length" class="estimate-doc__empty">
                {{ $t('publicQuote.noDetails') }}
              </div>
              <div v-else class="estimate-doc__total">
                <span>{{ $t('publicQuote.docTotal') }}</span>
                <strong>{{ formatUsd(quote.totalAmount) }}</strong>
              </div>
            </div>

            <div v-if="paymentPlans.length" class="estimate-doc__payment-plan">
              <div class="estimate-doc__title estimate-doc__title--sub">{{ $t('publicQuote.paymentPlanTitle') }}</div>
              <div class="estimate-doc__table-wrap estimate-doc__table-wrap--plan">
                <div class="estimate-doc__head estimate-doc__head--plan">
                  <span>{{ $t('publicQuote.colIndex') }}</span>
                  <span>{{ $t('publicQuote.colPlanNode') }}</span>
                  <span class="estimate-doc__amt">{{ $t('publicQuote.colAmount') }}</span>
                  <span>{{ $t('publicQuote.colPlanDate') }}</span>
                </div>
                <div
                  v-for="(item, index) in paymentPlans"
                  :key="item.nodeSeq || index"
                  class="estimate-doc__row estimate-doc__row--plan"
                >
                  <span class="estimate-doc__idx">{{ item.nodeSeq != null ? item.nodeSeq : index + 1 }}</span>
                  <span class="estimate-doc__product">{{ item.nodeName || '—' }}</span>
                  <span class="estimate-doc__amt">{{ formatUsd(item.plannedAmount) }}</span>
                  <span class="estimate-doc__desc">{{ item.plannedDate || '—' }}</span>
                </div>
                <div class="estimate-doc__total estimate-doc__total--plan">
                  <span>{{ $t('publicQuote.planTotal') }}</span>
                  <strong>{{ formatUsd(paymentPlanTotal) }}</strong>
                </div>
              </div>
            </div>
          </section>

          <section
            v-if="paymentPlans.length && !(showDetails && detailsExpanded)"
            class="estimate-doc estimate-doc--summary-plan"
          >
            <div class="estimate-doc__title estimate-doc__title--sub">{{ $t('publicQuote.paymentPlanTitle') }}</div>
            <div class="estimate-doc__table-wrap estimate-doc__table-wrap--plan">
              <div class="estimate-doc__head estimate-doc__head--plan">
                <span>{{ $t('publicQuote.colIndex') }}</span>
                <span>{{ $t('publicQuote.colPlanNode') }}</span>
                <span class="estimate-doc__amt">{{ $t('publicQuote.colAmount') }}</span>
                <span>{{ $t('publicQuote.colPlanDate') }}</span>
              </div>
              <div
                v-for="(item, index) in paymentPlans"
                :key="'sp-' + (item.nodeSeq || index)"
                class="estimate-doc__row estimate-doc__row--plan"
              >
                <span class="estimate-doc__idx">{{ item.nodeSeq != null ? item.nodeSeq : index + 1 }}</span>
                <span class="estimate-doc__product">{{ item.nodeName || '—' }}</span>
                <span class="estimate-doc__amt">{{ formatUsd(item.plannedAmount) }}</span>
                <span class="estimate-doc__desc">{{ item.plannedDate || '—' }}</span>
              </div>
              <div class="estimate-doc__total estimate-doc__total--plan">
                <span>{{ $t('publicQuote.planTotal') }}</span>
                <strong>{{ formatUsd(paymentPlanTotal) }}</strong>
              </div>
            </div>
          </section>

          <div v-if="customerCanDecide" class="decision-box">
            <div class="decision-box__actions">
              <el-button class="accept-button" type="success" @click="decide(true)">{{ $t('publicQuote.accept') }}</el-button>
              <el-button type="text" class="decline-button" @click="decide(false)">{{ $t('publicQuote.decline') }}</el-button>
            </div>
          </div>

          <div v-else-if="!isAccepted" class="decision-hint">{{ customerDecisionHint }}</div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { formatUsd } from '@/utils/formatUsd'
import { resolveApiBase } from '@/utils/apiBase'
import { formatDisplayDateTime } from '@/utils/formatDisplayDateTime'

const baseURL = resolveApiBase()

export default {
  name: 'PublicQuote',
  data() {
    return {
      loading: false,
      quote: null,
      details: [],
      paymentPlans: [],
      showDetails: false,
      detailsExpanded: false
    }
  },
  computed: {
    isAccepted() {
      const q = this.quote
      if (!q) return false
      return Number(q.quoteStatusCode) === 5
    },
    customerDisplayName() {
      const q = this.quote
      if (!q) return '—'
      const parts = [q.customerFirstName, q.customerLastName].filter(
        x => x != null && String(x).trim() !== ''
      )
      return parts.length ? parts.map(x => String(x).trim()).join(' ') : '—'
    },
    introText() {
      if (!this.showDetails) return this.$t('publicQuote.summaryModeIntro')
      if (!this.detailsExpanded) return this.$t('publicQuote.detailModeIntroCollapsed')
      return this.$t('publicQuote.detailModeIntro')
    },
    acceptedOnDisplay() {
      const q = this.quote
      if (!q || !q.customerDecisionAt) return '—'
      const s = formatDisplayDateTime(q.customerDecisionAt)
      return s || '—'
    },
    customerCanDecide() {
      const q = this.quote
      if (!q) return false
      const qs = Number(q.quoteStatusCode)
      if (Number.isNaN(qs) || qs < 4) return false
      if (qs === 5 || qs === 6) return false
      if (qs !== 4 && qs !== 7) return false
      const cd = q.customerDecisionCode
      return cd == null || Number(cd) === 1
    },
    customerDecisionHint() {
      const q = this.quote
      if (!q) return ''
      const qs = Number(q.quoteStatusCode)
      if (qs === 5) return this.$t('publicQuote.accepted')
      if (qs === 6) return this.$t('publicQuote.rejected')
      if (qs < 4) return this.$t('publicQuote.notSentYet')
      return this.$t('publicQuote.decisionUnavailable')
    },
    paymentPlanTotal() {
      return (this.paymentPlans || []).reduce((acc, p) => acc + (Number(p.plannedAmount) || 0), 0)
    }
  },
  created() {
    this.load()
  },
  methods: {
    formatUsd,
    lineNo(item, index) {
      const n = item && item.lineNo != null ? Number(item.lineNo) : index + 1
      return Number.isFinite(n) && n > 0 ? n : index + 1
    },
    toggleDetails() {
      this.detailsExpanded = !this.detailsExpanded
      this.$nextTick(() => {
        if (this.detailsExpanded && this.$refs.detailsSection) {
          this.$refs.detailsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    async load() {
      const token = this.$route.params.token
      this.loading = true
      try {
        const r = await axios.get(`${baseURL}/api/public/quote/${token}`)
        const b = r.data
        const d = (b && b.data) ? b.data : b
        this.quote = d.quote
        this.details = Array.isArray(d.details) ? d.details : []
        this.paymentPlans = Array.isArray(d.paymentPlans) ? d.paymentPlans : []
        this.showDetails = !!d.showDetails
        this.detailsExpanded = false
      } catch (e) {
        this.$message.error('Unable to load quote')
      } finally {
        this.loading = false
      }
    },
    async decide(approve) {
      const token = this.$route.params.token
      try {
        await axios.post(`${baseURL}/api/public/quote/${token}/decision`, {
          approve
        })
        this.$message.success(this.$t('publicQuote.submitted'))
        this.load()
      } catch (e) {
        this.$message.error('Submit failed')
      }
    }
  }
}
</script>

<style scoped>
.public-quote-page {
  min-height: 100vh;
  padding: 28px 12px;
  background: #f4f6f8;
  color: #111827;
}

.public-quote-shell {
  max-width: 720px;
  margin: 0 auto;
}

.public-quote-logo {
  margin-bottom: 16px;
  color: #2563eb;
  font-size: 16px;
  font-weight: 700;
}

.public-quote-card {
  border-radius: 12px;
  border: 0;
  box-shadow: 0 14px 40px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

/* --- Accepted celebration (QuickBooks-style) --- */
.celebration {
  position: relative;
  padding: 28px 20px 22px;
  text-align: center;
  background: linear-gradient(180deg, #ecfdf5 0%, #ffffff 55%);
  border-bottom: 3px solid #2563eb;
  overflow: hidden;
}

.celebration__confetti {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    radial-gradient(circle at 12% 18%, rgba(37, 99, 235, 0.45) 0%, transparent 28%),
    radial-gradient(circle at 88% 12%, rgba(59, 130, 246, 0.35) 0%, transparent 24%),
    radial-gradient(circle at 72% 28%, rgba(168, 85, 247, 0.3) 0%, transparent 20%),
    radial-gradient(circle at 32% 8%, rgba(20, 184, 166, 0.35) 0%, transparent 22%),
    radial-gradient(circle at 50% 22%, rgba(234, 179, 8, 0.25) 0%, transparent 18%);
  opacity: 0.85;
}

.celebration__title {
  position: relative;
  font-size: 26px;
  font-weight: 800;
  color: #1d4ed8;
  letter-spacing: -0.02em;
}

.celebration__subtitle {
  position: relative;
  margin-top: 6px;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.celebration__meta {
  position: relative;
  max-width: 380px;
  margin: 18px auto 0;
  text-align: left;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.85);
}

.celebration__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  font-size: 13px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.celebration__row:last-child {
  border-bottom: 0;
}

.celebration__k {
  color: #64748b;
  font-weight: 600;
}

.celebration__v {
  color: #0f172a;
  font-weight: 600;
  text-align: right;
}

.celebration__next {
  position: relative;
  margin: 16px auto 0;
  max-width: 400px;
  font-size: 13px;
  line-height: 1.6;
  color: #64748b;
}

.estimate-hero {
  padding: 30px 18px 16px;
  text-align: center;
}

.hero-label {
  color: #94a3b8;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.hero-amount {
  margin-top: 8px;
  color: #0f172a;
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.03em;
}

.hero-toggle {
  margin-top: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #0284c7;
  padding: 8px 12px;
}

.move-forward {
  max-width: 430px;
  margin: 0 auto 20px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.65;
  text-align: center;
}

/* --- PDF-style estimate breakdown --- */
.estimate-doc {
  margin: 0 12px 24px;
  padding: 0 0 8px;
}

.estimate-doc__title {
  font-size: 18px;
  font-weight: 800;
  color: #2563eb;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.estimate-doc__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
  padding: 16px 18px;
  background: #e8f4fc;
  border-radius: 8px;
  border: 1px solid #cfe8f7;
  margin-bottom: 16px;
}

.estimate-doc__meta-col {
  min-width: 160px;
  flex: 1;
}

.estimate-doc__meta-col--right {
  text-align: right;
}

.estimate-doc__label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 6px;
}

.estimate-doc__value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.4;
}

.estimate-doc__kv {
  font-size: 13px;
  color: #0f172a;
  margin-bottom: 6px;
}

.estimate-doc__kv:last-child {
  margin-bottom: 0;
}

.estimate-doc__k {
  color: #64748b;
  font-weight: 600;
  margin-right: 6px;
}

.estimate-doc__v {
  font-weight: 700;
}

.estimate-doc__title--sub {
  font-size: 14px;
  margin-top: 20px;
}

.estimate-doc--summary-plan {
  margin: 0 12px 24px;
}

.estimate-doc__payment-plan {
  margin-top: 8px;
}

.estimate-doc__head--plan,
.estimate-doc__row--plan {
  grid-template-columns: 36px minmax(96px, 1fr) 88px 100px;
  min-width: 420px;
}

.estimate-doc__total--plan {
  display: flex;
  justify-content: space-between;
  padding: 12px 14px;
  background: #f8fafc;
  border-top: 1px solid #cbd5e1;
  font-size: 13px;
}

.estimate-doc__table-wrap {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  overflow-x: auto;
  background: #fff;
}

.estimate-doc__head,
.estimate-doc__row {
  display: grid;
  grid-template-columns: 36px minmax(96px, 1fr) minmax(120px, 1.35fr) 88px;
  gap: 12px;
  align-items: start;
  padding: 12px 14px;
  font-size: 12px;
  min-width: 520px;
}

.estimate-doc__head {
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.estimate-doc__row {
  border-bottom: 1px solid #e2e8f0;
}

.estimate-doc__row:last-of-type {
  border-bottom: 0;
}

.estimate-doc__idx {
  color: #64748b;
  font-weight: 700;
}

.estimate-doc__product {
  font-weight: 700;
  color: #1e3a5f;
  line-height: 1.45;
}

.estimate-doc__desc {
  color: #475569;
  line-height: 1.55;
  white-space: pre-wrap;
}

.estimate-doc__amt {
  text-align: right;
  font-weight: 600;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.estimate-doc__empty {
  padding: 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}

.estimate-doc__total {
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  gap: 16px;
  padding: 14px 16px;
  border-top: 1px solid #cbd5e1;
  background: #f8fafc;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  min-width: 520px;
}

.estimate-doc__total strong {
  font-size: 18px;
  font-weight: 800;
}

.decision-box {
  max-width: 430px;
  margin: 0 auto;
  padding-top: 18px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.decision-box__actions {
  margin-top: 0;
  text-align: center;
}

.accept-button {
  display: block;
  width: 100%;
  max-width: 380px;
  margin: 0 auto 10px;
  background: #00a300;
  border-color: #00a300;
  font-weight: 700;
}

.decline-button {
  color: #0284c7;
  font-weight: 600;
}

.decision-hint {
  margin: 22px auto 0;
  max-width: 430px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.08);
  color: #64748b;
  font-size: 13px;
  text-align: center;
}

@media (max-width: 640px) {
  .estimate-doc__head {
    display: none;
  }

  .estimate-doc__row {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 14px;
    border-bottom: 1px solid #e2e8f0;
    min-width: 0;
  }

  .estimate-doc__idx {
    font-size: 11px;
    color: #94a3b8;
  }

  .estimate-doc__amt {
    text-align: left;
    padding-top: 4px;
    border-top: 1px dashed #e2e8f0;
    margin-top: 4px;
  }

  .estimate-doc__meta-col--right {
    text-align: left;
  }

  .estimate-doc__total {
    min-width: 0;
  }

  .hero-amount {
    font-size: 28px;
  }
}
</style>
